import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'

export default function BaristaArchive({ branchId, onBack, onBaristaRestored }) {
    const [archivedBaristas, setArchivedBaristas] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [employmentTypeFilter, setEmploymentTypeFilter] = useState('All')
    const [showViewModal, setShowViewModal] = useState(false)
    const [viewingBarista, setViewingBarista] = useState(null)

    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
            .swal2-confirm { background-color: #FF6F3C !important; border: none !important; }
            .swal2-confirm:hover { background-color: #ff4c0b !important; }
            .swal2-cancel { background-color: #6b7280 !important; border: none !important; }
            .swal2-cancel:hover { background-color: #5a5a5a !important; }
        `
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])

    useEffect(() => {
        if (branchId) fetchArchivedBaristas()
    }, [branchId])

    const getFriendlyError = (err) => {
        const msg = err?.message?.toLowerCase() || ''
        if (msg.includes('network') || msg.includes('fetch'))
            return 'Network error. Please check your internet connection and try again.'
        if (msg.includes('jwt') || msg.includes('auth') || msg.includes('unauthorized'))
            return 'Your session has expired. Please log in again.'
        if (msg.includes('permission') || msg.includes('rls'))
            return "You don't have permission to perform this action."
        return 'Something went wrong. Please try again or contact support.'
    }

    const fetchArchivedBaristas = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('barista_info')
                .select('*')
                .eq('branch_id', branchId)
                .eq('status', 'Inactive')
                .order('updated_at', { ascending: false })

            if (error) throw error
            setArchivedBaristas(data || [])
        } catch (err) {
            console.error('Error fetching archived baristas:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Load Archive', text: getFriendlyError(err), confirmButtonColor: '#FF6F3C' })
        } finally {
            setLoading(false)
        }
    }

    const handleRestoreClick = (barista) => {
        Swal.fire({
            icon: 'question',
            title: `Restore ${barista.first_name} ${barista.last_name}?`,
            text: 'This will set their status back to Active.',
            showCancelButton: true,
            confirmButtonText: 'Yes, restore',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#10b981',
        }).then(async (result) => {
            if (!result.isConfirmed) return
            try {
                const { error } = await supabase
                    .from('barista_info')
                    .update({ status: 'Active' })
                    .eq('barista_id', barista.barista_id)

                if (error) throw error

                Swal.fire({ icon: 'success', title: 'Barista Restored', text: `${barista.first_name} ${barista.last_name} is now Active.`, confirmButtonColor: '#FF6F3C', timer: 3000 })
                fetchArchivedBaristas()
                if (onBaristaRestored) onBaristaRestored()
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Failed to Restore', text: getFriendlyError(err), confirmButtonColor: '#FF6F3C' })
            }
        })
    }

    const filtered = archivedBaristas.filter(b => {
        const matchesSearch = `${b.first_name} ${b.last_name} ${b.email}`.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesEmploymentType = employmentTypeFilter === 'All' || b.employment_type === employmentTypeFilter
        return matchesSearch && matchesEmploymentType
    })

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-8">
            <div className="bg-white rounded-lg shadow-sm">

            {/* Header section */}
            <div className="p-6 border-b border-gray-200">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium transition mb-4"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8l8 8l1.41-1.41L7.83 13H20z"/>
                    </svg>
                    Back to Branch Baristas
                </button>

                <h2 className="text-xl font-bold text-gray-800">Archived Baristas</h2>
                <p className="text-sm text-gray-500 mt-0.5">Baristas set to Inactive for your branch</p>
            </div>

            {/* Search + filters section */}
            <div className="p-6 border-b border-gray-200">
                <div className="relative max-w-md">
                    <input
                        type="text"
                        placeholder="Search archived baristas..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6F3C]"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6"/>
                        </svg>
                    </span>
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-4 mt-4">
                    {['All', 'Full-time', 'Part-time', 'Contract'].map((type) => (
                        <button
                            key={type}
                            onClick={() => setEmploymentTypeFilter(type)}
                            className={`px-4 py-2 rounded-lg font-medium ${
                                employmentTypeFilter === type
                                    ? 'bg-[#FF6F3C] text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barista</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employment Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">Loading archived baristas...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No archived baristas found.</td></tr>
                            ) : (
                                filtered.map((barista) => (
                                    <tr key={barista.barista_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="font-medium text-gray-800">{barista.first_name} {barista.last_name}</p>
                                            <p className="text-sm text-gray-500">{barista.email}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">{barista.employment_type}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                                            {new Date(barista.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => { setViewingBarista(barista); setShowViewModal(true) }}
                                                    className="p-1 text-gray-600 hover:bg-gray-100 rounded" title="View Details">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 5.92A.96.96 0 1 0 12 4a.96.96 0 0 0 0 1.92m0 7.04a.96.96 0 1 0 0-1.92a.96.96 0 0 0 0 1.92M12 20a.96.96 0 1 0 0-1.92a.96.96 0 0 0 0 1.92"/>
                                                    </svg>
                                                </button>
                                                <button onClick={() => handleRestoreClick(barista)}
                                                    className="p-1 text-green-600 hover:bg-green-50 rounded" title="Restore">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                        <path fill="currentColor" d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89l.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7s-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18m-1 5v5l4.25 2.52l.77-1.28l-3.52-2.09V8z"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showViewModal && viewingBarista && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => { setShowViewModal(false); setViewingBarista(null) }} />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Archived Barista Details</h2>
                            <p className="text-sm text-gray-600 mt-1">Full information for this barista</p>
                        </div>
                        <div className="px-6 py-6 space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">First Name</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.first_name}</p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Last Name</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.last_name}</p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.email}</p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Phone Number</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.phone_number}</p></div>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Employment Details</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Employment Type</label>
                                        <p className="text-gray-800 font-medium">{viewingBarista.employment_type}</p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Start Date</label>
                                        <p className="text-gray-800 font-medium">
                                            {new Date(viewingBarista.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                        </p></div>
                                    <div><label className="block text-sm font-medium text-gray-600 mb-1">Barista ID</label>
                                        <p className="text-gray-800 font-mono text-sm">{viewingBarista.barista_id}</p></div>
                                </div>
                            </div>
                        </div>
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-between">
                            <button onClick={() => { setShowViewModal(false); handleRestoreClick(viewingBarista); setViewingBarista(null) }}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">
                                Restore Barista
                            </button>
                            <button onClick={() => { setShowViewModal(false); setViewingBarista(null) }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}